import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  DashboardOutlined,
  TeamOutlined,
  LineChartOutlined,
  KeyOutlined,
  SettingOutlined,
  LogoutOutlined,
  MenuOutlined,
  CloseOutlined,
  UserOutlined,
  RightOutlined
} from '@ant-design/icons';

interface EnterpriseShellProps {
  children: React.ReactNode;
  tenantName: string;
  onLogout: () => void;
  userEmail: string;
}

export const EnterpriseShell: React.FC<EnterpriseShellProps> = ({
  children,
  tenantName,
  onLogout,
  userEmail
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    {
      path: '/enterprise/dashboard',
      icon: <DashboardOutlined style={{ fontSize: 20 }} />,
      label: 'HR Dashboard',
    },
    {
      path: '/enterprise/candidates',
      icon: <TeamOutlined style={{ fontSize: 20 }} />,
      label: 'Candidate Screening',
    },
    {
      path: '/enterprise/dynamics',
      icon: <LineChartOutlined style={{ fontSize: 20 }} />,
      label: 'Team Dynamics Map',
    },
    {
      path: '/enterprise/api-keys',
      icon: <KeyOutlined style={{ fontSize: 20 }} />,
      label: 'Developer API Keys',
    },
    {
      path: '/enterprise/settings',
      icon: <SettingOutlined style={{ fontSize: 20 }} />,
      label: 'Organization Settings',
    },
  ];

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col font-sans text-slate-900">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Top Header navbar */}
      <header className="bg-white border-b-2 border-slate-200 h-16 px-4 md:px-6 flex justify-between items-center sticky top-0 z-40">
        <div className="flex items-center gap-3 min-w-0">
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-slate-100 rounded-md transition-colors border border-slate-200 lg:hidden flex-shrink-0"
            aria-label={sidebarOpen ? 'Tutup menu' : 'Buka menu'}
          >
            {sidebarOpen ? <CloseOutlined style={{ fontSize: 20 }} /> : <MenuOutlined style={{ fontSize: 20 }} />}
          </button>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-base md:text-lg font-bold tracking-tight uppercase bg-slate-900 text-white px-2.5 md:px-3 py-1 rounded truncate">
              BaZi Insights
            </span>
            <span className="hidden sm:inline text-xs font-semibold bg-indigo-50 text-indigo-700 px-2 py-0.5 rounded-full border border-indigo-200 whitespace-nowrap">
              ENTERPRISE HR Enterprise
            </span>
          </div>
        </div>

        <div className="flex items-center gap-3 md:gap-4 flex-shrink-0">
          <div className="hidden md:flex flex-col text-right">
            <span className="text-sm font-semibold text-slate-700 truncate max-w-[160px]">{tenantName}</span>
            <span className="text-xs text-slate-500 truncate max-w-[160px]">{userEmail}</span>
          </div>

          <div className="relative group">
            <button className="flex items-center justify-center w-9 h-9 rounded-full bg-slate-100 border-2 border-slate-300 text-slate-700 hover:bg-slate-200 transition-colors">
              <UserOutlined style={{ fontSize: 18 }} />
            </button>
            <div className="absolute right-0 mt-2 w-48 bg-white border border-slate-200 rounded-md shadow-lg py-1 hidden group-hover:block z-50">
              <div className="px-4 py-2 border-b border-slate-100 text-xs text-slate-500 truncate md:hidden">
                {userEmail}
              </div>
              <button 
                onClick={onLogout}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
              >
                <LogoutOutlined style={{ fontSize: 16 }} />
                Sign Out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Layout wrapper */}
      <div className="flex flex-1 relative">
        {/* Sidebar */}
        <aside className={`
          bg-slate-900 text-slate-300 border-r border-slate-800
          fixed inset-y-16 left-0 z-30 w-64
          transform transition-all duration-300 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
          lg:transform-none lg:translate-x-0
        `}>
          <nav className="p-3 md:p-4 space-y-1.5 overflow-y-auto h-full pb-20">
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    closeSidebar();
                  }}
                  className={`
                    w-full flex items-center justify-between px-4 py-3 rounded-md text-sm font-medium transition-all
                    ${isActive 
                      ? 'bg-slate-800 text-white border-l-4 border-indigo-500 pl-3' 
                      : 'hover:bg-slate-800/50 hover:text-white'}
                  `}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="flex-shrink-0">{item.icon}</span>
                    <span className="truncate">{item.label}</span>
                  </div>
                  {isActive && <RightOutlined style={{ fontSize: 14, color: '#818cf8' }} className="flex-shrink-0" />}
                </button>
              );
            })}
          </nav>

          {/* Bottom logout block */}
          <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4 border-t border-slate-800 bg-slate-950">
            <button 
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded bg-slate-850 hover:bg-red-950/20 hover:text-red-400 hover:border-red-900/50 border border-slate-800 transition-colors text-xs font-semibold"
            >
              <LogoutOutlined style={{ fontSize: 14 }} />
              Sign Out from Portal
            </button>
          </div>
        </aside>

        {/* Content body */}
        <main className="flex-1 min-w-0 p-4 md:p-6 lg:pl-64 bg-slate-50 overflow-y-auto">
          <div className="max-w-7xl mx-auto">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};
export default EnterpriseShell;
